import { AtlasBreadcrumb, AtlasPageHeader, CampusHeading } from "atlas-ds"
import Text from "components/text"
import Head from "next/head"
import Link from "next/link"

export default function Sitemap() {
    const title = "Déclaration d’accessibilité - campusAtlas"
    const description = "Atlas s’engage à rendre ses services en ligne accessibles."

    return <>
        <Head>
            <title key="__title">{ title }</title>
            <meta property="og:title" content={ title } key="__og:title" />
            <meta name="description" content={ description } key="__description" />
            <meta property="og:description" content={ description } key="__og:description" />
        </Head>

        <Text
            pageHeader={<AtlasPageHeader
                breadcrumb={<AtlasBreadcrumb
                    parts={[{href: '/', label: 'Accueil'}]}
                    currentPageLabel="Déclaration d'accessibilité"
                />}
            >
                <CampusHeading tag="h1">Déclaration d'accessibilité</CampusHeading>
            </AtlasPageHeader>}
        >
            <p>Atlas s’engage à rendre ses services en ligne accessibles conformément à l’article 47 de la loi n°&nbsp;2005-102 du 11 février 2005.</p>
            <p>A cette fin, il met en œuvre la stratégie et les actions suivantes : <Link href="/accessibilite/plan-action">voir le plan d'action</Link>.</p>
            <p>Cette déclaration d’accessibilité s’applique au service en ligne <i>campusAtlas</i>.</p>

            <h2>État de conformité</h2>
            <p>Le service en ligne <i>campusAtlas</i> est en conformité partielle avec le référentiel général d’amélioration de l’accessibilité en raison des non-conformités énumérées ci-dessous.</p>

            <h2>Résultats des tests</h2>
            <p>L’audit de conformité réalisé en interne révèle que :</p>

            <ul>
                <li>74% des critères RGAA applicables sont respectés.</li>
                <li>Le taux moyen de conformité du service en ligne s’élève à 96%.</li>
            </ul>

            <p>
                <a href="/audit-rgaa.ods">Télécharger le rapport de test au format ods</a>
            </p>

            <h2>Contenus non accessibles</h2>

            <p>Les contenus listés ci-dessous ne sont pas accessibles pour les raisons suivantes.</p>

            <h3>Non conformité</h3>

            <p>Note : ces problèmes sont progressivement résolus depuis l'établissement de cette déclaration. La liste sera mise à jour lors du prochain audit.</p>

            <ul>
                <li>
                    <q>Critère 1.2. Chaque image de décoration est-elle correctement ignorée par les technologies d’assistance ?</q>
                    <p>Certaines images de décoration des pages Contact et Mentions légales ne sont pas marquées comme telles.</p>
                </li>
                <li>
                    <q>Critère 1.3 : Pour chaque image porteuse d'information ayant une alternative textuelle, cette alternative est-elle pertinente (hors cas particuliers) ?</q>
                    <p>La page de connexion ne respecte pas ce critère.</p>
                </li>
                <li>
                    <q>Critère 6.1. Chaque lien est-il explicite (hors cas particuliers) ?</q>
                    <p>Certains liens des pages Contact et Mentions légales ne respectent pas ce critère.</p>
                </li>
                <li>
                    <q>Critère 7.3 : Chaque script est-il contrôlable par le clavier et par tout dispositif de pointage ?</q>
                    <p>Sur les pages Contact et Mentions légales, le menu principal ne peut pas être fermé au clavier.</p>
                </li>
                <li>
                    <q>Critère 8.2. Pour chaque page web, le code source généré est-il valide selon le type de document spécifié ?</q>
                    <p>Le validateur du W3C ne valide pas le code HTML des pages Contact et Mentions légales.</p>
                </li>
                <li>
                    <q>Critère 9.1 : Dans chaque page web, l'information est-elle structurée par l'utilisation appropriée de titres ?</q>
                    <p>La page de connexion et les pages Contact et Mentions légales ne respectent pas ce critère.</p>
                </li>
                <li>
                    <q>Critère 9.2 : Dans chaque page web, la structure du document est-elle cohérente (hors cas particuliers) ?</q>
                    <p>La page de connexion ne respecte pas ce critère.</p>
                </li>
                <li>
                    <q>Critère 10.5. Dans chaque page web, les déclarations CSS de couleurs de fond d’élément et de police sont-elles correctement utilisées ?</q>
                    <p>Les pages Contact et Mentions légales ne déclarent pas leur couleur de fond.</p>
                </li>
                <li>
                    <q>Critère 10.8. Pour chaque page web, les contenus cachés ont-ils vocation à être ignorés par les technologies d’assistance ?</q>
                    <p>Les pages Contact et Mentions légales cachent du contenu en utilisant des techniques inappropriées.</p>
                </li>
                <li>
                    <q>Critère 10.13. Dans chaque page web, les contenus additionnels apparaissant à la prise de focus ou au survol d’un composant d’interface sont-ils contrôlables par l’utilisateur (hors cas particuliers) ?</q>
                    <p>Dans les pages Contact et Mentions légales, si on clique sur le menu au lieu de le survoler, on ferme le menu et on ne peut plus l'ouvrir.</p>
                </li>
                <li>
                    <q>Critère 11.10 : Dans chaque formulaire, le contrôle de saisie est-il utilisé de manière pertinente (hors cas particuliers) ? </q>
                    <p>La page de connexion ne présente pas correctement les champs obligatoires et les formats attendus.</p>
                </li>
                <li>
                    <q>Critère 11.13 : La finalité d'un champ de saisie peut-elle être déduite pour faciliter le remplissage automatique des champs avec les données de l'utilisateur ?</q>
                    <p>La page de connexion n'utilise pas le mécanisme d'autocomplétion des navigateurs.</p>
                </li>
                <li>
                    <q>Critère 12.6. Les zones de regroupement de contenus présentes dans plusieurs pages web (zones d’en-tête, de navigation principale, de contenu principal, de pied de page et de moteur de recherche) peuvent-elles être atteintes ou évitées ? </q>
                    <p>La page de connexion ne respecte pas ce critère.</p>
                </li>
                <li>
                    <q>Critère 12.8. Dans chaque page web, l’ordre de tabulation est-il cohérent ?</q>
                    <p>Sur la page de connexion, il est impossible de placer le focus sur les deux champs et le bouton de soumission au clavier.</p>
                </li>
                <li>
                    <q>Critère 13.9. Dans chaque page web, le contenu proposé est-il consultable quelle que soit l’orientation de l’écran (portrait ou paysage) (hors cas particuliers) ?</q>
                    <p>Dans les pages Contact et Mentions légales, le menu Prestataire contient des éléments qui sortent de la zone visible de l’écran.</p>
                </li>
            </ul>

            <h2>Établissement de cette déclaration d'accessibilité</h2>

            <p>Cette déclaration a été établie le <time dateTime="2022-03-17">17 mars 2022</time>. Elle a été mise à jour le <time dateTime="2022-05-03">3 mai 2022</time>.</p>

            <h3>Technologies utilisées pour la réalisation du site web :</h3>

            <ul>
                <li>HTML</li>
                <li>CSS</li>
                <li>Javascript</li>
                <li>Next.js</li>
            </ul>

            <h3>Agents utilisateurs, technologies d’assistance et outils utilisés pour vérifier l’accessibilité</h3>

            <p>Les tests des pages web ont été effectués avec les combinaisons de navigateurs web et lecteurs d’écran suivants :</p>

            <ul>
                <li>Chrome 99 et Orca</li>
            </ul>

            <p>Les outils suivants ont été utilisés lors de l’évaluation :</p>
            <ul>
                <li>Inspection manuelle des pages et du code source</li>
                <li>Plugin Axe DevTools</li>
                <li>Plugin Taiko Accessibility</li>
                <li>Validateur HTML W3C</li>
                <li>Vérificateur de contrastes WebAIM</li>
            </ul>

            <p>Pages du site ayant fait l’objet de la vérification de conformité :</p>

            <ul>
                <li>
                    <Link href="/">
                        Accueil
                    </Link>
                </li>
                <li>
                    <Link href="/landing">
                        <i>campusAtlas</i>, c'est quoi
                    </Link>
                </li>
                <li>
                    <Link href="/branches">
                        Branches
                    </Link>
                </li>
                <li>Page Branche (URL dynamique)</li>
                <li>Domaine (URL dynamique)</li>
                <li>Thématique (URL dynamique)</li>
                <li>Module (URL dynamique)</li>
                <li>Stage (URL dynamique)</li>
                <li>Organisme (URL dynamique)</li>
                <li>
                    <Link href="/accessibilite" aria-current="true">
                        Accessibilité
                    </Link>
                </li>
                <li>
                    <Link href="/plan-du-site">
                        Plan du site
                    </Link>
                </li>
                <li>Authentification (page externe, URL dynamique)</li>
                <li>
                    <a href="https://www.opco-atlas.fr/contact.html" rel="noopener noreferrer">Contact (page externe)</a>
                </li>
                <li>
                    <a href="https://www.opco-atlas.fr/mentions-legales.html" rel="noopener noreferrer">Mention légale (page externe)</a>
                </li>
            </ul>

            <h2>Retour d'information et contact</h2>

            <p>Si pour des raisons d’accessibilité vous n’arrivez pas à atteindre des contenus et services compréhensibles et utilisables vous pouvez envoyer un mail à <a href="mailto:webassistance@opco-atlas.fr">webassistance@opco-atlas.fr</a> pour qu’une alternative puisse être identifiée afin de corriger l’accessibilité ou obtenir le contenu sous une autre forme ou format.</p>

            <h2>Voie de recours</h2>

            <p>Cette procédure est à utiliser dans le cas suivant.</p>
            <p>Vous avez signalé au responsable du site internet un défaut d’accessibilité qui vous empêche d’accéder à un contenu ou à un des services du portail et vous n’avez pas obtenu de réponse satisfaisante.</p>

            <ul>
                <li>
                    <a href="https://formulaire.defenseurdesdroits.fr/">Écrire un message au Défenseur des droits</a>
                </li>
                <li>
                    <a href="https://www.defenseurdesdroits.fr/saisir/delegues">Contacter le délégué du Défenseur des droits dans votre région</a>
                </li>
                <li>
                    Envoyer un courrier par la poste (gratuit, ne pas mettre de timbre) Défenseur des droits Libre réponse 71120 75342 Paris CEDEX 07
                </li>
            </ul>
        </Text>
    </>
}
